/**
 * PreviewSavedReportDialog
 *
 * Renders a saved Infralytiqs SDK embed snippet verbatim inside a sandboxed
 * iframe, authenticated with a short-lived preview token minted by the server.
 * Loads the real Infralytiqs SDK from CDN so the preview exercises the same
 * IL_TOKEN → _il_token → x-il-token path as a production host page.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, Preview as PreviewIcon, Refresh,
} from '@mui/icons-material';
import { RootState } from 'state/store';
import apiClient from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

const PREVIEW_TOKEN_ENDPOINT = '/il/share/report-suites/preview-token';
const INFRALYTIQS_SDK_CDN = 'https://assets.infralytiqs.com/cdn/infralytiqs.min.js';

interface SavedShareForPreview {
  _id: string;
  name: string;
  snippet: string;
}

interface PreviewSavedReportDialogProps {
  open: boolean;
  onClose: () => void;
  share: SavedShareForPreview | null;
}

/** Escape a value for safe inclusion in a double-quoted JS string inside srcdoc. */
const escapeJsString = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

const buildPreviewSrcDoc = (
  tokenId: string,
  snippet: string,
  googleMapsBrowserJSApiKey?: string,
): string => {
  const mapsKey = (googleMapsBrowserJSApiKey || '').trim();
  const embedConfigScript = mapsKey
    ? `<script>window.IL_EMBED_CONFIG = {"googleMapsBrowserJSApiKey":"${escapeJsString(mapsKey)}"};</script>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<script>window.IL_TOKEN = "${escapeJsString(tokenId)}";</script>
${embedConfigScript}
<script src="${INFRALYTIQS_SDK_CDN}"></script>
</head>
<body style="margin:0;padding:0;">
${snippet}
</body>
</html>`;
};

export const PreviewSavedReportDialog: React.FC<PreviewSavedReportDialogProps> = ({
  open,
  onClose,
  share,
}) => {
  const googleMapsBrowserJSApiKey = useSelector(
    (state: RootState) => state.clientConfig.data?.googleMapsBrowserJSApiKey as string | undefined,
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenId, setTokenId] = useState('');
  const [iframeKey, setIframeKey] = useState(0);

  const mintPreviewToken = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.post<{ tokenId: string; expiresIn?: number }>(PREVIEW_TOKEN_ENDPOINT);
      const id = res?.data?.tokenId;
      if (!id) {
        throw new Error('Preview token was not returned by the server');
      }
      setTokenId(id);
      setIframeKey((k) => k + 1);
    } catch (err: any) {
      Logger.error('PreviewSavedReportDialog', `Failed to mint preview token: ${err}`);
      setError(err?.message || 'Failed to start preview');
      setTokenId('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !share) {
      setTokenId('');
      setError('');
      setIframeKey(0);
      return;
    }
    void mintPreviewToken();
  }, [open, share, mintPreviewToken]);

  const srcDoc = useMemo(() => {
    if (!tokenId || !share?.snippet) return '';
    return buildPreviewSrcDoc(tokenId, share.snippet, googleMapsBrowserJSApiKey);
  }, [tokenId, share?.snippet, googleMapsBrowserJSApiKey]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <PreviewIcon fontSize="small" />
          <Typography variant="h6" component="span" fontWeight={600} noWrap>
            Preview: {share?.name || 'Saved report'}
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Alert severity="info" sx={{ py: 0.5 }}>
          Live preview using a temporary Infralytiqs token and the real SDK from CDN.
          The saved snippet runs unchanged inside the iframe.
        </Alert>

        {error && <Alert severity="error">{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress color="secondary" />
          </Box>
        )}

        {!loading && !error && srcDoc && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'background.default',
              minHeight: '75vh',
            }}
          >
            <iframe
              key={iframeKey}
              title={`Preview ${share?.name || 'saved report'}`}
              sandbox="allow-scripts allow-same-origin"
              srcDoc={srcDoc}
              style={{ width: '100%', height: '75vh', border: 0, display: 'block' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
          onClick={() => void mintPreviewToken()}
          disabled={loading || !share}
        >
          Refresh preview
        </Button>
        <Button variant="contained" color="secondary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewSavedReportDialog;
